import {AiChatWindowAnchor} from '../state/ai-chat-session-state.store';

export type AiChatResizeCorner = 'nw' | 'ne' | 'sw' | 'se';

export interface AiChatWindowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AiChatViewportSize {
  width: number;
  height: number;
}

export interface AiChatWindowInsets {
  insetX: number;
  insetY: number;
}

/** Сохранённая позиция: left/top для совместимости + якорь и отступы от выбранного угла. */
export interface AiChatAnchoredPosition extends AiChatWindowInsets {
  x: number;
  y: number;
  anchor: AiChatWindowAnchor;
}

const ANCHORS: readonly AiChatWindowAnchor[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

export function isAiChatWindowAnchor(value: unknown): value is AiChatWindowAnchor {
  return typeof value === 'string' && ANCHORS.includes(value as AiChatWindowAnchor);
}

export function hasAnchorInsets(
  position: {anchor?: unknown; insetX?: unknown; insetY?: unknown} | null | undefined,
): position is AiChatAnchoredPosition {
  return Boolean(
    position
    && isAiChatWindowAnchor(position.anchor)
    && Number.isFinite(position.insetX)
    && Number.isFinite(position.insetY),
  );
}

/** Квадрант по центру окна относительно центра вьюпорта. На середине — правый/нижний. */
export function resolveWindowAnchor(
  rect: AiChatWindowRect,
  viewport: AiChatViewportSize,
): AiChatWindowAnchor {
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const horizontal = centerX < viewport.width / 2 ? 'left' : 'right';
  const vertical = centerY < viewport.height / 2 ? 'top' : 'bottom';

  return `${vertical}-${horizontal}` as AiChatWindowAnchor;
}

export function insetsFromPosition(
  rect: AiChatWindowRect,
  viewport: AiChatViewportSize,
  anchor: AiChatWindowAnchor,
): AiChatWindowInsets {
  switch (anchor) {
    case 'top-left':
      return {insetX: rect.x, insetY: rect.y};
    case 'top-right':
      return {insetX: viewport.width - rect.x - rect.width, insetY: rect.y};
    case 'bottom-left':
      return {insetX: rect.x, insetY: viewport.height - rect.y - rect.height};
    case 'bottom-right':
      return {
        insetX: viewport.width - rect.x - rect.width,
        insetY: viewport.height - rect.y - rect.height,
      };
  }
}

export function positionFromInsets(
  insets: AiChatWindowInsets,
  size: {width: number; height: number},
  viewport: AiChatViewportSize,
  anchor: AiChatWindowAnchor,
): {x: number; y: number} {
  switch (anchor) {
    case 'top-left':
      return {x: insets.insetX, y: insets.insetY};
    case 'top-right':
      return {x: viewport.width - size.width - insets.insetX, y: insets.insetY};
    case 'bottom-left':
      return {x: insets.insetX, y: viewport.height - size.height - insets.insetY};
    case 'bottom-right':
      return {
        x: viewport.width - size.width - insets.insetX,
        y: viewport.height - size.height - insets.insetY,
      };
  }
}

/**
 * Держит окно во вьюпорте. Если окно больше экрана — оставляет отрицательный left/top,
 * чтобы сохранить привязку к противоположному краю.
 */
export function clampWindowPosition(
  x: number,
  y: number,
  size: {width: number; height: number},
  viewport: AiChatViewportSize,
): {x: number; y: number} {
  return {
    x: clampAxis(x, size.width, viewport.width),
    y: clampAxis(y, size.height, viewport.height),
  };
}

export function clampWidgetSize(
  width: number,
  height: number,
  limits: {minWidth: number; maxWidth: number; minHeight: number; maxHeight: number},
): {width: number; height: number} {
  return {
    width: Math.min(limits.maxWidth, Math.max(limits.minWidth, width)),
    height: Math.min(limits.maxHeight, Math.max(limits.minHeight, height)),
  };
}

/** Новый размер и left/top при растягивании из угла: противоположный угол остаётся на месте. */
export function resizeFromCorner(
  start: AiChatWindowRect,
  pointerDelta: {x: number; y: number},
  corner: AiChatResizeCorner,
  limits: {minWidth: number; maxWidth: number; minHeight: number; maxHeight: number},
): AiChatWindowRect {
  let {x, y, width, height} = start;

  if (corner.includes('e')) {
    width = start.width + pointerDelta.x;
  } else {
    width = start.width - pointerDelta.x;
  }

  if (corner.includes('s')) {
    height = start.height + pointerDelta.y;
  } else {
    height = start.height - pointerDelta.y;
  }

  const clamped = clampWidgetSize(width, height, limits);
  width = clamped.width;
  height = clamped.height;

  if (corner.includes('w')) {
    x = start.x + start.width - width;
  }

  if (corner.includes('n')) {
    y = start.y + start.height - height;
  }

  return {x, y, width, height};
}

export function captureAnchoredPosition(
  rect: AiChatWindowRect,
  viewport: AiChatViewportSize,
): AiChatAnchoredPosition {
  const clamped = clampWindowPosition(rect.x, rect.y, rect, viewport);
  const nextRect = {...rect, ...clamped};
  const anchor = resolveWindowAnchor(nextRect, viewport);
  const insets = insetsFromPosition(nextRect, viewport, anchor);

  return {
    x: nextRect.x,
    y: nextRect.y,
    anchor,
    insetX: insets.insetX,
    insetY: insets.insetY,
  };
}

function clampAxis(value: number, size: number, viewport: number): number {
  const min = Math.min(0, viewport - size);
  const max = Math.max(0, viewport - size);

  return Math.max(min, Math.min(value, max));
}
